import { lazy, Suspense, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import RequireAuth from "@/components/auth/RequireAuth";
import RequireVoskiAuth from "@/components/auth/RequireVoskiAuth";
import RequirePartnerAuth from "@/components/auth/RequirePartnerAuth";
import RequireWelcome from "@/components/auth/RequireWelcome";
import WelcomePageGuard from "@/components/auth/WelcomePageGuard";
import RootLayout from "@/components/layouts/RootLayout";
import BlankLayout from "@/components/layouts/BlankLayout";
import { Loading } from "@/components/ui/loading";
import { RouterProvider, createBrowserRouter, Navigate, useNavigate } from "react-router-dom";
import ErrorBoundary, { ErrorElement } from "@/components/common/ErrorBoundary";

// Safari-optimized lazy loading with better error handling
const safariLazyLoad = (importFunc: () => Promise<any>) => {
  return lazy(() => 
    importFunc().catch(error => {
      console.error('Safari lazy loading error:', error);
      // Return a fallback component for Safari
      return { default: () => <div>Loading failed. Please refresh.</div> };
    })
  );
};

// Lazy load components with Safari optimization
const Home = safariLazyLoad(() => import("@/components/home"));
const WelcomePage = safariLazyLoad(() => import("@/components/welcome/WelcomePage"));
const AdminLayout = safariLazyLoad(() => import("@/components/admin/AdminLayout"));
const VoskiLayout = safariLazyLoad(() => import("@/components/admin/VoskiLayout"));
const PartnerLayout = safariLazyLoad(() => import("@/components/partner/PartnerLayout"));
const PartnerReturnRequests = safariLazyLoad(() => import("@/components/partner/ReturnRequests"));
const Dashboard = safariLazyLoad(() => import("@/components/admin/Dashboard"));
const CollectionsManagement = safariLazyLoad(() => import("@/components/admin/collections/CollectionsManagement"));
const ProductsManagement = safariLazyLoad(() => import("@/components/admin/products/ProductsManagement"));
const CategoryManagement = safariLazyLoad(() => import("@/components/admin/categories/CategoryManagement"));
const LoginPage = safariLazyLoad(() => import("@/components/auth/LoginPage"));
const SignUpPage = safariLazyLoad(() => import("@/components/auth/SignUpPage"));
const VerifyEmailPage = safariLazyLoad(() => import("@/components/auth/VerifyEmailPage"));
const ForgotPasswordPage = safariLazyLoad(() => import("@/components/auth/ForgotPasswordPage"));
const ResetPasswordPage = safariLazyLoad(() => import("@/components/auth/ResetPasswordPage"));
const CartPage = safariLazyLoad(() => import("@/components/cart/CartPage"));
const CheckoutPage = safariLazyLoad(() => import("@/components/checkout/CheckoutPage"));
const OrdersPage = safariLazyLoad(() => import("@/components/orders/OrdersPage"));
const OrderDetailsPage = safariLazyLoad(() => import("@/components/orders/OrderDetailsPage"));
const NotFoundPage = safariLazyLoad(() => import("@/components/NotFoundPage"));
const TalesPage = safariLazyLoad(() => import("@/components/tales/TalesPage"));
const TaleDetails = safariLazyLoad(() => import("@/components/tales/TaleDetails"));
const FragrancesPage = safariLazyLoad(() => import("@/components/fragrances/FragrancesPage"));
const FragranceDetails = safariLazyLoad(() => import("@/components/fragrances/FragranceDetails"));
const ProductDetail = safariLazyLoad(() => import("@/components/products/ProductDetail"));
const FAQ = safariLazyLoad(() => import("@/components/pages/FAQ"));
const AdminReturnRequests = safariLazyLoad(() => import("@/components/admin/fragrance/ReturnRequests"));
const HomepageEditor = safariLazyLoad(() => import("@/components/admin/HomepageEditor"));
const UsersManagement = safariLazyLoad(() => import("@/components/admin/users/UsersManagement"));
const OrdersManagement = safariLazyLoad(() => import("@/components/admin/orders/OrdersManagement"));
const TalesManagement = safariLazyLoad(() => import("@/components/admin/tales/TalesManagement"));
const OrderConfirmation = safariLazyLoad(() => import("@/components/checkout/OrderConfirmation"));
const ProfilePage = safariLazyLoad(() => import("@/components/profile/ProfilePage"));
const ProfileDetails = safariLazyLoad(() => import("@/components/profile/ProfileDetails"));
const OrderHistory = safariLazyLoad(() => import("@/components/profile/OrderHistory"));
const AddressBook = safariLazyLoad(() => import("@/components/profile/AddressBook"));
const AccountSettings = safariLazyLoad(() => import("@/components/profile/AccountSettings"));
const ProductCare = safariLazyLoad(() => import("@/components/pages/ProductCare"));
const PrivacyPage = safariLazyLoad(() => import("@/pages/PrivacyPage"));
const StoryGenerator = safariLazyLoad(() => import("@/components/admin/stories/StoryGenerator"));
const FragranceManagement = safariLazyLoad(() => import("@/components/admin/fragrance/FragranceManagement"));
const LoadingDemoPage = safariLazyLoad(() => import("@/components/pages/LoadingDemoPage"));
const PartnerSalesStats = safariLazyLoad(() => import("@/components/partner/PartnerSalesStats"));
const PartnerCodes = safariLazyLoad(() => import("@/components/partner/PartnerCodes"));
const PartnerFinance = safariLazyLoad(() => import("@/components/partner/PartnerFinance"));
const PartnerStock = safariLazyLoad(() => import("@/components/partner/PartnerStock"));
const ReturnsListPage = safariLazyLoad(() => import("@/app/returns/index"));
const ReturnCreatePage = safariLazyLoad(() => import("@/app/returns/create"));
const ReturnDetailsPage = safariLazyLoad(() => import("@/app/returns/[returnId]/page"));
const AdminReturnsPage = safariLazyLoad(() => import("@/app/admin/returns/index"));
const AdminReturnDetailsPage = safariLazyLoad(() => import("@/app/admin/returns/[returnId]/page"));
const ReturnsAnalyticsPage = safariLazyLoad(() => import("@/app/admin/returns-analytics/page"));
const ProductsPage = safariLazyLoad(() => import("@/components/products/ProductsPage"));

// Wrapper components for protected routes that need user data
const ProtectedProfileDetails = () => {
  const { user } = useAuth();
  return user ? <ProfileDetails user={user} /> : null;
};

const ProtectedOrderHistory = () => {
  const { user } = useAuth();
  return user ? <OrderHistory userId={user.id} /> : null;
};

const ProtectedAccountSettings = () => {
  const { user } = useAuth();
  return user ? <AccountSettings userId={user.id} /> : null;
};

// Wrapper for admin routes with error boundary
const AdminErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary 
    fallback={
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Admin Panel Error</h2>
        <p className="text-gray-700 mb-4">There was a problem loading the admin panel. This could be due to a network issue or a problem with the application.</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

// Separate error boundary for partner routes
const PartnerErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary 
    fallback={
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-blue-600 mb-4">Partner Dashboard Error</h2>
        <p className="text-gray-700 mb-4">There was a problem loading your partner dashboard. This could be due to a temporary network issue or a problem with your account configuration.</p>
        <div className="flex space-x-4">
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Return to Home
          </button>
        </div>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

export const routes = [
  // Welcome page in its own layout (no navbar/footer)
  {
    path: "/welcome", 
    element: <BlankLayout />,
    errorElement: <ErrorElement />,
    children: [
      { 
        index: true,
        element: (
          <WelcomePageGuard>
            <Suspense fallback={<Loading />}>
              <WelcomePage />
            </Suspense>
          </WelcomePageGuard>
        )
      }
    ]
  },
  // Main app with navbar/footer
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorElement />,
    children: [
      // Homepage - protected to ensure first-time visitors see welcome page
      { 
        index: true, 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <Home />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      
      // Protected product routes - ensure welcome seen first
      { 
        path: "products", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <ProductsPage />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      { 
        path: "products/:productId", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <ProductDetail />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      
      // Other product and content routes - ensure welcome seen first
      { 
        path: "tales", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <TalesPage />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      { 
        path: "tales/:taleId", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <TaleDetails />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      { 
        path: "fragrances", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <FragrancesPage />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      { 
        path: "fragrances/:fragranceId", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <FragranceDetails />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      { 
        path: "cart", 
        element: (
          <RequireWelcome>
            <Suspense fallback={<Loading />}>
              <CartPage />
            </Suspense>
          </RequireWelcome>
        ) 
      },
      { path: "faq", element: <Suspense fallback={<Loading />}><FAQ /></Suspense> },
      { path: "product-care", element: <Suspense fallback={<Loading />}><ProductCare /></Suspense> },
      { path: "privacy", element: <Suspense fallback={<Loading />}><PrivacyPage /></Suspense> },
      
      // Profile routes
      { path: "profile", element: <Suspense fallback={<Loading />}><ProfilePage /></Suspense> },
      { path: "profile/details", element: <RequireAuth><Suspense fallback={<Loading />}><ProtectedProfileDetails /></Suspense></RequireAuth> },
      { path: "profile/orders", element: <RequireAuth><Suspense fallback={<Loading />}><ProtectedOrderHistory /></Suspense></RequireAuth> },
      { path: "profile/addresses", element: <RequireAuth><Suspense fallback={<Loading />}><AddressBook /></Suspense></RequireAuth> },
      { path: "profile/settings", element: <RequireAuth><Suspense fallback={<Loading />}><ProtectedAccountSettings /></Suspense></RequireAuth> },
      
      // Auth routes
      { path: "login", element: <Suspense fallback={<Loading />}><LoginPage /></Suspense> },
      { path: "signup", element: <Suspense fallback={<Loading />}><SignUpPage /></Suspense> },
      { path: "verify-email", element: <Suspense fallback={<Loading />}><VerifyEmailPage /></Suspense> },
      { path: "forgot-password", element: <Suspense fallback={<Loading />}><ForgotPasswordPage /></Suspense> },
      { path: "reset-password", element: <Suspense fallback={<Loading />}><ResetPasswordPage /></Suspense> },
      
      // Protected routes
      { 
        path: "checkout", 
        element: <RequireAuth><Suspense fallback={<Loading />}><CheckoutPage /></Suspense></RequireAuth> 
      },
      { 
        path: "orders", 
        element: <RequireAuth><Suspense fallback={<Loading />}><OrdersPage /></Suspense></RequireAuth> 
      },
      { 
        path: "orders/:orderId", 
        element: <RequireAuth><Suspense fallback={<Loading />}><OrderDetailsPage /></Suspense></RequireAuth> 
      },
      { path: "order-confirmation/:orderId", element: <Suspense fallback={<Loading />}><OrderConfirmation /></Suspense> },
      
      // Returns routes
      { 
        path: "returns", 
        element: <RequireAuth><Suspense fallback={<Loading />}><ReturnsListPage /></Suspense></RequireAuth> 
      },
      { 
        path: "returns/create/:orderId", 
        element: <RequireAuth><Suspense fallback={<Loading />}><ReturnCreatePage /></Suspense></RequireAuth> 
      },
      { 
        path: "returns/:returnId", 
        element: <RequireAuth><Suspense fallback={<Loading />}><ReturnDetailsPage /></Suspense></RequireAuth> 
      },
      
      // Admin routes
      {
        path: "/admin",
        element: 
          <RequireAuth requireAdmin>
            <AdminErrorBoundary>
              <Suspense fallback={<Loading />}>
                <AdminLayout />
              </Suspense>
            </AdminErrorBoundary>
          </RequireAuth>,
        children: [
          { index: true, element: <Dashboard /> },
          { path: "products", element: <ProductsManagement /> },
          { path: "categories", element: <CategoryManagement /> },
          { path: "collections", element: <CollectionsManagement /> },
          { path: "homepage", element: <HomepageEditor /> },
          { path: "users", element: <UsersManagement /> },
          { path: "orders", element: <OrdersManagement /> },
          { path: "tales", element: <TalesManagement /> },
          { path: "story-generator", element: <StoryGenerator /> },
          { path: "returns", element: <AdminReturnsPage /> },
          { path: "returns/:returnId", element: <AdminReturnDetailsPage /> },
          { path: "returns-analytics", element: <ReturnsAnalyticsPage /> },
          { path: "fragrance", element: <FragranceManagement /> },
          { path: "fragrance/returns", element: <AdminReturnRequests /> },
        ],
      },
      
      // VOSKI Partner Route
      {
        path: "/voski",
        element: 
          <RequireVoskiAuth>
            <AdminErrorBoundary>
              <Suspense fallback={<Loading />}>
                <VoskiLayout />
              </Suspense>
            </AdminErrorBoundary>
          </RequireVoskiAuth>,
        children: [
          { index: true, element: <FragranceManagement /> },
          { path: "fragrance", element: <FragranceManagement /> }
        ],
      },
      
      // Partner Dashboard Route - Using BlankLayout to avoid the main header
      {
        path: "/partner",
        element: <BlankLayout />,
        children: [
          {
            path: "",
            element: 
              <RequirePartnerAuth>
                <PartnerErrorBoundary>
                  <Suspense fallback={<Loading />}>
                    <PartnerLayout />
                  </Suspense>
                </PartnerErrorBoundary>
              </RequirePartnerAuth>,
            children: [
              // Redirect index to codes as the default view
              { index: true, element: <Navigate to="/partner/codes" replace /> },
              // Add redirect for the old route that was in use
              { path: "fragrance", element: <Navigate to="/partner/codes" replace /> },
              { path: "codes", element: <PartnerCodes /> },
              { path: "returns", element: <PartnerReturnRequests /> },
              { path: "sales", element: <PartnerSalesStats /> },
              { path: "finance", element: <PartnerFinance /> },
              { path: "stock", element: <PartnerStock /> }
            ],
          }
        ],
      },
      
      // 404 route
      { path: "*", element: <Suspense fallback={<Loading />}><NotFoundPage /></Suspense> }
    ]
  },
  {
    path: "/loading-demo",
    element: <RootLayout />,
    errorElement: <ErrorElement />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loading />}>
            <LoadingDemoPage />
          </Suspense>
        ),
      },
    ],
  },
];

export default function App() {
  return (
    <RouterProvider router={createBrowserRouter(routes)} />
  );
}
